// ========================================================================
// 🏥 SERVICIO DE TAREAS DE ENFERMERÍA
// ========================================================================

import axios from 'axios';
import {
  API_BASE_NURSING_TASKS_URL,
  API_NURSING_TASKS_LIST,
  API_NURSING_TASKS_DETAILS,
  API_NURSING_TASKS_CREATE,
  API_NURSING_TASKS_UPDATE,
  API_NURSING_TASKS_DELETE,
  API_NURSING_TASKS_DAILY,
  API_NURSING_TASKS_OVERDUE,
  API_NURSING_TASKS_BY_PATIENT,
  API_NURSING_TASKS_COMPLETE,
  API_NURSING_TASKS_CANCEL,
  API_NURSING_TASKS_START
} from '../../../helpers/urlHelper';

class NursingTasksService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_NURSING_TASKS_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para manejar errores globalmente
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('Error en NursingTasksService:', error);
        return Promise.reject(error);
      }
    );
  }

  // ========================================================================
  // OPERACIONES CRUD BÁSICAS
  // ========================================================================

  /**
   * Obtener todas las tareas de enfermería
   * @param {Object} params - Parámetros de consulta opcionales
   * @returns {Promise} Lista de tareas
   */
  async getAllTasks(params = {}) {
    try {
      const response = await this.api.get(API_NURSING_TASKS_LIST, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener las tareas: ${error.message}`);
    }
  }

  /**
   * Obtener una tarea específica por ID
   * @param {string} taskId - ID de la tarea
   * @returns {Promise} Datos de la tarea
   */
  async getTaskById(taskId) {
    try {
      const response = await this.api.get(`${API_NURSING_TASKS_DETAILS}/${taskId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener la tarea: ${error.message}`);
    }
  }

  /**
   * Crear una nueva tarea de enfermería
   * @param {Object} taskData - Datos de la tarea
   * @returns {Promise} Tarea creada
   */
  async createTask(taskData) {
    try {
      const response = await this.api.post(API_NURSING_TASKS_CREATE, taskData);
      return response.data;
    } catch (error) {
      throw new Error(`Error al crear la tarea: ${error.message}`);
    }
  }

  /**
   * Actualizar una tarea existente
   * @param {string} taskId - ID de la tarea
   * @param {Object} taskData - Datos actualizados
   * @returns {Promise} Tarea actualizada
   */
  async updateTask(taskId, taskData) {
    try {
      const response = await this.api.put(`${API_NURSING_TASKS_UPDATE}/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      throw new Error(`Error al actualizar la tarea: ${error.message}`);
    }
  }

  /**
   * Eliminar una tarea
   * @param {string} taskId - ID de la tarea
   * @returns {Promise} Confirmación de eliminación
   */
  async deleteTask(taskId) {
    try {
      const response = await this.api.delete(`${API_NURSING_TASKS_DELETE}/${taskId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al eliminar la tarea: ${error.message}`);
    }
  }

  // ========================================================================
  // CONSULTAS ESPECÍFICAS
  // ========================================================================

  /**
   * Obtener tareas del día actual
   * @returns {Promise} Lista de tareas del día
   */
  async getDailyTasks() {
    try {
      const response = await this.api.get(API_NURSING_TASKS_DAILY);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener las tareas del día: ${error.message}`);
    }
  }

  /**
   * Obtener tareas vencidas
   * @returns {Promise} Lista de tareas vencidas
   */
  async getOverdueTasks() {
    try {
      const response = await this.api.get(API_NURSING_TASKS_OVERDUE);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener las tareas vencidas: ${error.message}`);
    }
  }

  /**
   * Obtener tareas de un paciente específico
   * @param {string} patientId - ID del paciente
   * @returns {Promise} Lista de tareas del paciente
   */
  async getTasksByPatient(patientId) {
    try {
      const response = await this.api.get(`${API_NURSING_TASKS_BY_PATIENT}/${patientId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener las tareas del paciente: ${error.message}`);
    }
  }

  // ========================================================================
  // ACCIONES ESPECÍFICAS DE TAREAS
  // ========================================================================

  /**
   * Completar una tarea
   * @param {string} taskId - ID de la tarea
   * @param {Object} completionData - Datos de finalización (performedBy, notes)
   * @returns {Promise} Tarea completada
   */
  async completeTask(taskId, completionData) {
    try {
      const response = await this.api.patch(
        `${API_NURSING_TASKS_COMPLETE}/${taskId}/complete`,
        completionData
      );
      return response.data;
    } catch (error) {
      throw new Error(`Error al completar la tarea: ${error.message}`);
    }
  }

  /**
   * Cancelar una tarea
   * @param {string} taskId - ID de la tarea
   * @param {Object} cancellationData - Datos de cancelación (reason)
   * @returns {Promise} Tarea cancelada
   */
  async cancelTask(taskId, cancellationData = {}) {
    try {
      const response = await this.api.patch(
        `${API_NURSING_TASKS_CANCEL}/${taskId}/cancel`,
        cancellationData
      );
      return response.data;
    } catch (error) {
      throw new Error(`Error al cancelar la tarea: ${error.message}`);
    }
  }

  /**
   * Iniciar una tarea
   * @param {string} taskId - ID de la tarea
   * @param {Object} startData - Datos de inicio (performedBy)
   * @returns {Promise} Tarea iniciada
   */
  async startTask(taskId, startData) {
    try {
      const response = await this.api.patch(
        `${API_NURSING_TASKS_START}/${taskId}/start`,
        startData
      );
      return response.data;
    } catch (error) {
      throw new Error(`Error al iniciar la tarea: ${error.message}`);
    }
  }

  // ========================================================================
  // MÉTODOS AUXILIARES
  // ========================================================================

  /**
   * Configurar token de autenticación
   * @param {string} token - Token de autenticación
   */
  setAuthToken(token) {
    if (token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  /**
   * Configurar headers personalizados
   * @param {Object} headers - Headers adicionales
   */
  setCustomHeaders(headers) {
    Object.assign(this.api.defaults.headers, headers);
  }
}

// Crear instancia singleton del servicio
const nursingTasksService = new NursingTasksService();

export default nursingTasksService;